import type { FilterId } from "../types";

// How a selection goes on the wire: multi -> string[], single -> string,
// boolean -> true/false, integer -> number.
export type RespondentAdvancedShape =
  | "multi"
  | "single"
  | "boolean"
  | "integer";

// "alwaysHard": Respondent filters on it whenever set; it is not a valid keyQualifiers key.
// "signal": left as Respondent's soft preference; our screener does the qualifying.
type RespondentAdvancedMatching = "alwaysHard" | "signal";

type RespondentAdvancedAttribute = {
  key: string;
  title: string;
  category: string;
  question: string;
  shape: RespondentAdvancedShape;
  matching: RespondentAdvancedMatching;
  choices: Record<string, string>;
};

const OTHER = { other: "Other" };

const YES_NO = { true: "Yes", false: "No" };

// Device ownership only makes sense as a requirement, so "No" is not offered.
const REQUIRED = { true: "Required" };

const CHILD_COUNT_CHOICES = Object.fromEntries(
  ["0", "1", "2", "3", "4", "5"].map((n) => [n, n])
);

const CHILDREN_BIRTH_YEAR_CHOICES = Object.fromEntries(
  Array.from({ length: 50 }, (_, i) => {
    const year = String(new Date().getFullYear() - i);
    return [year, year];
  })
);

// Keys are Respondent's exact advancedTargeting attribute names and enum values (sent on the wire).
export const RESPONDENT_ADVANCED_ATTRIBUTES = [
  {
    key: "relationshipStatus",
    title: "Relationship status",
    category: "Personal",
    question: "Which relationship status should participants have?",
    shape: "single",
    matching: "signal",
    choices: {
      single: "Single",
      inRelationship: "In a relationship",
      married: "Married",
      divorced: "Divorced",
      widowed: "Widowed",
      separated: "Separated",
      domesticPartnershipCivilUnion: "Domestic partnership / civil union",
      itsComplicated: "It's complicated",
      preferNotToSay: "Prefer not to say",
      ...OTHER,
    },
  },
  {
    key: "languages",
    title: "Languages",
    category: "Personal",
    question: "Which languages should participants speak?",
    shape: "multi",
    matching: "signal",
    choices: {
      en: "English",
      es: "Spanish",
      zh: "Chinese",
      hi: "Hindi",
      ar: "Arabic",
      bn: "Bengali",
      ru: "Russian",
      pt: "Portuguese",
      ja: "Japanese",
      de: "German",
      fr: "French",
      it: "Italian",
      ko: "Korean",
      tr: "Turkish",
      pl: "Polish",
      nl: "Dutch",
      vi: "Vietnamese",
      sv: "Swedish",
      fa: "Persian",
      el: "Greek",
      da: "Danish",
      fi: "Finnish",
      no: "Norwegian",
      cs: "Czech",
      th: "Thai",
      ro: "Romanian",
      he: "Hebrew",
      id: "Indonesian",
      hu: "Hungarian",
      uk: "Ukrainian",
      ...OTHER,
    },
  },
  {
    key: "nationality",
    title: "Nationality",
    category: "Personal",
    question: "Which nationalities should participants have?",
    shape: "multi",
    matching: "signal",
    choices: {
      american: "American",
      canadian: "Canadian",
      mexican: "Mexican",
      british: "British",
      french: "French",
      german: "German",
      italian: "Italian",
      spanish: "Spanish",
      dutch: "Dutch",
      swedish: "Swedish",
      norwegian: "Norwegian",
      danish: "Danish",
      finnish: "Finnish",
      polish: "Polish",
      irish: "Irish",
      portuguese: "Portuguese",
      greek: "Greek",
      swiss: "Swiss",
      austrian: "Austrian",
      belgian: "Belgian",
      czech: "Czech",
      romanian: "Romanian",
      hungarian: "Hungarian",
      chinese: "Chinese",
      indian: "Indian",
      japanese: "Japanese",
      korean: "Korean",
      filipino: "Filipino",
      vietnamese: "Vietnamese",
      thai: "Thai",
      malaysian: "Malaysian",
      singaporean: "Singaporean",
      indonesian: "Indonesian",
      pakistani: "Pakistani",
      bangladeshi: "Bangladeshi",
      australian: "Australian",
      newzealander: "New Zealander",
      brazilian: "Brazilian",
      argentinian: "Argentinian",
      colombian: "Colombian",
      chilean: "Chilean",
      peruvian: "Peruvian",
      venezuelan: "Venezuelan",
      southafrican: "South African",
      nigerian: "Nigerian",
      egyptian: "Egyptian",
      kenyan: "Kenyan",
      israeli: "Israeli",
      turkish: "Turkish",
      saudiarabian: "Saudi Arabian",
      ...OTHER,
    },
  },
  {
    key: "numberOfChildren",
    title: "Number of children",
    category: "Personal",
    question: "How many children should participants have?",
    shape: "integer",
    matching: "signal",
    choices: CHILD_COUNT_CHOICES,
  },
  {
    key: "numberOfChildrenLiveAtHome",
    title: "Children living at home",
    category: "Personal",
    question: "How many children should live at home with participants?",
    shape: "integer",
    matching: "signal",
    choices: CHILD_COUNT_CHOICES,
  },
  {
    key: "childrenBirthYears",
    title: "Children's birth years",
    category: "Personal",
    question: "In which years should participants' children be born?",
    shape: "multi",
    matching: "signal",
    choices: CHILDREN_BIRTH_YEAR_CHOICES,
  },
  {
    key: "livingSituation",
    title: "Living situation",
    category: "Personal",
    question: "Who should participants live with?",
    shape: "single",
    matching: "signal",
    choices: {
      alone: "Alone",
      roommates: "Roommates",
      significantOther: "Significant other",
      spouse: "Spouse",
      parents: "Parents",
      ...OTHER,
    },
  },
  {
    key: "homeOwner",
    title: "Home owner",
    category: "Personal",
    question: "Should participants own their home?",
    shape: "boolean",
    matching: "signal",
    choices: YES_NO,
  },
  {
    key: "pets",
    title: "Pets",
    category: "Personal",
    question: "Which pets should participants have?",
    shape: "multi",
    matching: "signal",
    choices: {
      noPets: "No pets",
      dog: "Dog",
      cat: "Cat",
      bird: "Bird",
      fish: "Fish",
      reptile: "Reptile",
      amphibian: "Amphibian",
      ...OTHER,
    },
  },
  {
    key: "areaType",
    title: "Area type",
    category: "Personal",
    question: "What kind of area should participants live in?",
    shape: "single",
    matching: "alwaysHard",
    choices: { urban: "Urban", suburban: "Suburban", rural: "Rural" },
  },
  {
    key: "onlineShoppingFrequency",
    title: "Online shopping frequency",
    category: "Personal",
    question: "How often should participants shop online?",
    shape: "single",
    matching: "signal",
    choices: {
      severaltimesweek: "Several times a week",
      aboutonceweek: "About once a week",
      fewtimesmonth: "A few times a month",
      rarely: "Rarely",
      never: "Never",
    },
  },
  {
    key: "militaryServiceStatus",
    title: "Military service",
    category: "Personal",
    question: "What military service status should participants have?",
    shape: "single",
    matching: "signal",
    choices: {
      neverserved: "Never served",
      currentlyserving: "Currently serving",
      veteran: "Veteran",
    },
  },
  {
    key: "workSetting",
    title: "Work setting",
    category: "Professional",
    question: "Where should participants work?",
    shape: "single",
    matching: "alwaysHard",
    choices: { hybrid: "Hybrid", inPerson: "In person", remote: "Remote" },
  },
  {
    key: "employmentStatuses",
    title: "Employment status",
    category: "Professional",
    question: "Which employment statuses are eligible?",
    shape: "multi",
    matching: "signal",
    choices: {
      owner: "Business owner",
      employedfulltime: "Employed full time",
      employedparttime: "Employed part time",
      unemployedlookingforwork: "Unemployed, looking for work",
      unemployed: "Unemployed",
      retired: "Retired",
      homemaker: "Homemaker",
      fulltimestudent: "Full-time student",
      parttimestudent: "Part-time student",
    },
  },
  {
    key: "cloudPlatforms",
    title: "Cloud platforms",
    category: "Professional",
    question: "Which cloud platforms should participants use?",
    shape: "multi",
    matching: "signal",
    choices: {
      aws: "AWS",
      microsoftazure: "Microsoft Azure",
      googlecloud: "Google Cloud",
      ibmcloud: "IBM Cloud",
      oraclecloud: "Oracle Cloud",
      alibabacloud: "Alibaba Cloud",
      digitalocean: "DigitalOcean",
      heroku: "Heroku",
      vercel: "Vercel",
      netlify: "Netlify",
      cloudflare: "Cloudflare",
      linode: "Linode",
      ...OTHER,
    },
  },
  {
    key: "codingAITools",
    title: "AI coding tools",
    category: "Professional",
    question: "Which AI coding tools should participants use?",
    shape: "multi",
    matching: "signal",
    choices: {
      claudecode: "Claude Code",
      cursor: "Cursor",
      windsurf: "Windsurf",
      githubcopilot: "GitHub Copilot",
      chatgpt: "ChatGPT",
      gemini: "Gemini",
      copilotchat: "Copilot Chat",
      ...OTHER,
    },
  },
  {
    key: "designPrototypingTools",
    title: "Design and prototyping tools",
    category: "Professional",
    question: "Which design or prototyping tools should participants use?",
    shape: "multi",
    matching: "signal",
    choices: {
      figma: "Figma",
      adobexd: "Adobe XD",
      sketch: "Sketch",
      invision: "InVision",
      framer: "Framer",
      axure: "Axure",
      balsamiq: "Balsamiq",
      marvel: "Marvel",
      protoio: "Proto.io",
      adobephotoshop: "Adobe Photoshop",
      adobeillustrator: "Adobe Illustrator",
      canva: "Canva",
      affinitydesigner: "Affinity Designer",
      principle: "Principle",
      origami: "Origami",
      ...OTHER,
    },
  },
  {
    key: "productivitySoftware",
    title: "Productivity software",
    category: "Professional",
    question: "Which productivity software should participants use?",
    shape: "multi",
    matching: "signal",
    choices: {
      microsoftoffice: "Microsoft Office",
      microsoft365: "Microsoft 365",
      googleworkspace: "Google Workspace",
      appleiwork: "Apple iWork",
      notion: "Notion",
      evernote: "Evernote",
      trello: "Trello",
      asana: "Asana",
      mondaydotcom: "monday.com",
      slack: "Slack",
      microsoftteams: "Microsoft Teams",
      zoom: "Zoom",
      airtable: "Airtable",
      clickup: "ClickUp",
      basecamp: "Basecamp",
      jira: "Jira",
      confluence: "Confluence",
      dropbox: "Dropbox",
      googledrive: "Google Drive",
      ...OTHER,
    },
  },
  {
    key: "browsers",
    title: "Web browsers",
    category: "Technology & devices",
    question: "Which web browsers should participants use?",
    shape: "multi",
    matching: "alwaysHard",
    choices: {
      chrome: "Chrome",
      edge: "Edge",
      firefox: "Firefox",
      internetExplorer: "Internet Explorer",
      safari: "Safari",
      ...OTHER,
    },
  },
  {
    key: "computerOS",
    title: "Computer operating system",
    category: "Technology & devices",
    question: "Which computer operating systems should participants use?",
    shape: "multi",
    matching: "alwaysHard",
    choices: {
      macOS: "macOS",
      windows: "Windows",
      linux: "Linux",
      noComputer: "No computer",
      ...OTHER,
    },
  },
  {
    key: "smartphoneOS",
    title: "Smartphone operating system",
    category: "Technology & devices",
    question: "Which smartphone operating systems should participants use?",
    shape: "multi",
    matching: "alwaysHard",
    choices: {
      android: "Android",
      ios: "iOS",
      windows: "Windows",
      noSmartphone: "No smartphone",
      ...OTHER,
    },
  },
  {
    key: "tabletOS",
    title: "Tablet operating system",
    category: "Technology & devices",
    question: "Which tablet operating systems should participants use?",
    shape: "multi",
    matching: "alwaysHard",
    choices: {
      amazonFire: "Amazon Fire",
      android: "Android",
      ipadOS: "iPadOS",
      windows: "Windows",
      noTablet: "No tablet",
      ...OTHER,
    },
  },
  {
    key: "programmingLanguages",
    title: "Programming languages",
    category: "Technology & devices",
    question: "Which programming languages should participants use?",
    shape: "multi",
    matching: "alwaysHard",
    choices: {
      javaScript: "JavaScript",
      typeScript: "TypeScript",
      python: "Python",
      java: "Java",
      "c#": "C#",
      "c++": "C++",
      ruby: "Ruby",
      php: "PHP",
      swift: "Swift",
      kotlin: "Kotlin",
      golang: "Go",
      r: "R",
      sql: "SQL",
      ...OTHER,
    },
  },
  {
    key: "webcam",
    title: "Webcam",
    category: "Technology & devices",
    question: "Must participants have a webcam?",
    shape: "boolean",
    matching: "alwaysHard",
    choices: REQUIRED,
  },
  {
    key: "headsetWithMicrophone",
    title: "Headset with microphone",
    category: "Technology & devices",
    question: "Must participants have a headset with a microphone?",
    shape: "boolean",
    matching: "alwaysHard",
    choices: REQUIRED,
  },
  {
    key: "mobilePhoneWithInternet",
    title: "Mobile phone with internet",
    category: "Technology & devices",
    question: "Must participants have a mobile phone with internet?",
    shape: "boolean",
    matching: "alwaysHard",
    choices: REQUIRED,
  },
  {
    key: "streamingServices",
    title: "Streaming services",
    category: "Products & services",
    question: "Which streaming services should participants use?",
    shape: "multi",
    matching: "signal",
    choices: {
      netflix: "Netflix",
      amazonprimevideo: "Amazon Prime Video",
      disneyplus: "Disney+",
      hulu: "Hulu",
      hbomax: "HBO Max",
      appletvplus: "Apple TV+",
      paramountplus: "Paramount+",
      peacock: "Peacock",
      youtubetv: "YouTube TV",
      youtubepremium: "YouTube Premium",
      crunchyroll: "Crunchyroll",
      discoveryplus: "discovery+",
      showtime: "Showtime",
      starz: "Starz",
      spotify: "Spotify",
      applemusic: "Apple Music",
      youtubemusic: "YouTube Music",
      amazonmusic: "Amazon Music",
      tidal: "Tidal",
      pandora: "Pandora",
      soundcloud: "SoundCloud",
      slingtv: "Sling TV",
      fubotv: "Fubo",
      ...OTHER,
    },
  },
  {
    key: "foodDeliveryServices",
    title: "Food delivery services",
    category: "Products & services",
    question: "Which food delivery services should participants use?",
    shape: "multi",
    matching: "signal",
    choices: {
      ubereats: "Uber Eats",
      doordash: "DoorDash",
      grubhub: "Grubhub",
      postmates: "Postmates",
      seamless: "Seamless",
      instacart: "Instacart",
      deliveroo: "Deliveroo",
      justeat: "Just Eat",
      skipthedishes: "SkipTheDishes",
      menulog: "Menulog",
      foodpanda: "foodpanda",
      zomato: "Zomato",
      swiggy: "Swiggy",
      rappi: "Rappi",
      ...OTHER,
    },
  },
  {
    key: "onlineBankingTools",
    title: "Banking and finance apps",
    category: "Products & services",
    question: "Which banking or finance apps should participants use?",
    shape: "multi",
    matching: "signal",
    choices: {
      chime: "Chime",
      varo: "Varo",
      current: "Current",
      revolut: "Revolut",
      n26: "N26",
      monzo: "Monzo",
      mint: "Mint",
      ynab: "YNAB",
      personalcapital: "Personal Capital",
      quicken: "Quicken",
      paypal: "PayPal",
      venmo: "Venmo",
      cashapp: "Cash App",
      zelle: "Zelle",
      applepay: "Apple Pay",
      googlepay: "Google Pay",
      robinhood: "Robinhood",
      webull: "Webull",
      acorns: "Acorns",
      stash: "Stash",
      betterment: "Betterment",
      wealthfront: "Wealthfront",
      coinbase: "Coinbase",
      binance: "Binance",
      kraken: "Kraken",
      ...OTHER,
    },
  },
  {
    key: "onlineLearningPlatforms",
    title: "Online learning platforms",
    category: "Products & services",
    question: "Which online learning platforms should participants use?",
    shape: "multi",
    matching: "signal",
    choices: {
      coursera: "Coursera",
      udemy: "Udemy",
      linkedinlearning: "LinkedIn Learning",
      skillshare: "Skillshare",
      edx: "edX",
      khanacademy: "Khan Academy",
      pluralsight: "Pluralsight",
      masterclass: "MasterClass",
      udacity: "Udacity",
      datacamp: "DataCamp",
      codecademy: "Codecademy",
      treehouse: "Treehouse",
      brilliant: "Brilliant",
      duolingo: "Duolingo",
      rosettastone: "Rosetta Stone",
      babbel: "Babbel",
      googleskillshop: "Google Skillshop",
      ...OTHER,
    },
  },
  {
    key: "onlineMarketplacePlatforms",
    title: "Online marketplaces",
    category: "Products & services",
    question: "Which online marketplaces should participants use?",
    shape: "multi",
    matching: "signal",
    choices: {
      amazon: "Amazon",
      ebay: "eBay",
      etsy: "Etsy",
      walmart: "Walmart",
      target: "Target",
      bestbuy: "Best Buy",
      alibaba: "Alibaba",
      aliexpress: "AliExpress",
      shopify: "Shopify",
      wayfair: "Wayfair",
      mercari: "Mercari",
      poshmark: "Poshmark",
      depop: "Depop",
      facebookmarketplace: "Facebook Marketplace",
      craigslist: "Craigslist",
      offerup: "OfferUp",
      letgo: "letgo",
      ...OTHER,
    },
  },
  {
    key: "onlineTravelBooking",
    title: "Travel booking sites",
    category: "Products & services",
    question: "Which travel booking sites should participants use?",
    shape: "multi",
    matching: "signal",
    choices: {
      expedia: "Expedia",
      bookingdotcom: "Booking.com",
      airbnb: "Airbnb",
      vrbo: "Vrbo",
      kayak: "Kayak",
      priceline: "Priceline",
      hotelsdotcom: "Hotels.com",
      tripadvisor: "Tripadvisor",
      orbitz: "Orbitz",
      travelocity: "Travelocity",
      hotwire: "Hotwire",
      skyscanner: "Skyscanner",
      agoda: "Agoda",
      tripdotcom: "Trip.com",
      hopper: "Hopper",
      googleflights: "Google Flights",
      ...OTHER,
    },
  },
  {
    key: "smartHomeDevices",
    title: "Smart home devices",
    category: "Products & services",
    question: "Which smart home devices should participants own?",
    shape: "multi",
    matching: "signal",
    choices: {
      amazonecho: "Amazon Echo",
      echoshow: "Echo Show",
      googlenest: "Google Nest",
      nesthub: "Nest Hub",
      nestthermostat: "Nest Thermostat",
      nestcam: "Nest Cam",
      applehomepod: "Apple HomePod",
      ecobee: "ecobee",
      honeywellhome: "Honeywell Home",
      philipshue: "Philips Hue",
      lifx: "LIFX",
      nanoleaf: "Nanoleaf",
      ring: "Ring",
      arlo: "Arlo",
      blink: "Blink",
      augustlock: "August lock",
      tplink: "TP-Link",
      wemo: "Wemo",
      kasa: "Kasa",
      roomba: "Roomba",
      roborock: "Roborock",
      eufy: "eufy",
      smartrefrigerator: "Smart refrigerator",
      smartoven: "Smart oven",
      smartwasher: "Smart washer",
      ...OTHER,
    },
  },
  {
    key: "socialNetworkPlatforms",
    title: "Social networks",
    category: "Products & services",
    question: "Which social networks should participants use?",
    shape: "multi",
    matching: "signal",
    choices: {
      facebook: "Facebook",
      instagram: "Instagram",
      twitter: "X (Twitter)",
      tiktok: "TikTok",
      linkedin: "LinkedIn",
      snapchat: "Snapchat",
      pinterest: "Pinterest",
      reddit: "Reddit",
      youtube: "YouTube",
      whatsapp: "WhatsApp",
      telegram: "Telegram",
      discord: "Discord",
      twitch: "Twitch",
      mastodon: "Mastodon",
      threads: "Threads",
      bluesky: "Bluesky",
      wechat: "WeChat",
      weibo: "Weibo",
      clubhouse: "Clubhouse",
      nextdoor: "Nextdoor",
      ...OTHER,
    },
  },
  {
    key: "vehicleBrands",
    title: "Vehicle brands",
    category: "Products & services",
    question: "Which vehicle brands should participants own?",
    shape: "multi",
    matching: "signal",
    choices: {
      ford: "Ford",
      chevrolet: "Chevrolet",
      dodge: "Dodge",
      ram: "Ram",
      jeep: "Jeep",
      gmc: "GMC",
      cadillac: "Cadillac",
      tesla: "Tesla",
      rivian: "Rivian",
      lucid: "Lucid",
      toyota: "Toyota",
      honda: "Honda",
      nissan: "Nissan",
      mazda: "Mazda",
      subaru: "Subaru",
      mitsubishi: "Mitsubishi",
      lexus: "Lexus",
      acura: "Acura",
      infiniti: "Infiniti",
      hyundai: "Hyundai",
      kia: "Kia",
      genesis: "Genesis",
      bmw: "BMW",
      mercedesbenz: "Mercedes-Benz",
      audi: "Audi",
      volkswagen: "Volkswagen",
      porsche: "Porsche",
      volvo: "Volvo",
      mini: "MINI",
      landrover: "Land Rover",
      jaguar: "Jaguar",
      fiat: "Fiat",
      alfaromeo: "Alfa Romeo",
      peugeot: "Peugeot",
      renault: "Renault",
      ferrari: "Ferrari",
      lamborghini: "Lamborghini",
      maserati: "Maserati",
      bentley: "Bentley",
      rollsroyce: "Rolls-Royce",
      ...OTHER,
    },
  },
  {
    key: "healthWellnessApps",
    title: "Health and wellness apps",
    category: "Health",
    question: "Which health or wellness apps should participants use?",
    shape: "multi",
    matching: "signal",
    choices: {
      applefitness: "Apple Fitness+",
      applehealth: "Apple Health",
      fitbit: "Fitbit",
      strava: "Strava",
      peloton: "Peloton",
      niketrainingclub: "Nike Training Club",
      myfitnesspal: "MyFitnessPal",
      loseit: "Lose It!",
      headspace: "Headspace",
      calm: "Calm",
      insighttimer: "Insight Timer",
      tenpercenthappier: "Ten Percent Happier",
      betterhelp: "BetterHelp",
      talkspace: "Talkspace",
      sleepcycle: "Sleep Cycle",
      slumber: "Slumber",
      googlefit: "Google Fit",
      samsunghealth: "Samsung Health",
      noom: "Noom",
      weightwatchers: "WeightWatchers",
      cronometer: "Cronometer",
      ...OTHER,
    },
  },
] as const satisfies readonly RespondentAdvancedAttribute[];

export type RespondentAdvancedAttributeKey =
  (typeof RESPONDENT_ADVANCED_ATTRIBUTES)[number]["key"];

export const getRespondentAdvancedFilterId = (
  key: RespondentAdvancedAttributeKey
): FilterId => `respondent.advanced.${key}` as FilterId;
